import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Html5Qrcode } from 'html5-qrcode';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { 
  Camera, CameraOff, Volume2, VolumeX, Maximize, X, 
  CheckCircle, XCircle, RefreshCw, Wifi, WifiOff, CloudOff,
  ScanLine, User, Building
} from 'lucide-react';
import { useOnlineStatus, queueOfflineAction } from '../components/OfflineIndicator';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckinStation = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { t } = useTranslation();
  
  const [event, setEvent] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [pendingOffline, setPendingOffline] = useState(0);
  const [cameraList, setCameraList] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);
  const successSound = useRef(null);
  const errorSound = useRef(null);

  // Initialize audio on component mount
  useEffect(() => {
    // Create audio context for better mobile support
    const createAudio = (frequency, duration) => {
      return new Promise((resolve) => {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
        
        resolve({ audioContext, oscillator, gainNode });
      });
    };

    successSound.current = () => createAudio(880, 0.15).then(() => createAudio(1174.66, 0.2));
    errorSound.current = () => createAudio(330, 0.3);

    // Get available cameras
    Html5Qrcode.getCameras().then(cameras => {
      setCameraList(cameras);
      // Prefer back camera on mobile
      const backCamera = cameras.find(c => 
        c.label.toLowerCase().includes('back') || 
        c.label.toLowerCase().includes('rear') ||
        c.label.toLowerCase().includes('environment')
      );
      setSelectedCamera(backCamera?.id || cameras[0]?.id);
    }).catch(err => {
      console.error('Failed to get cameras:', err);
    });

    fetchEvent();
    fetchStats();
    
    // Auto-refresh stats every 10 seconds
    const interval = setInterval(fetchStats, 10000);
    
    // Check pending offline scans
    checkPendingOffline();
    
    return () => {
      clearInterval(interval);
      stopScanner();
    };
  }, [eventId]);

  // Sync when coming back online
  useEffect(() => {
    if (isOnline && pendingOffline > 0) {
      syncOfflineCheckins();
    }
  }, [isOnline, pendingOffline]);

  const checkPendingOffline = () => {
    try {
      const pending = localStorage.getItem('eventflow_pending_sync');
      if (pending) {
        const items = JSON.parse(pending).filter(i => i.url.includes('/checkin') || i.url.includes('/scan'));
        setPendingOffline(items.length);
      }
    } catch (e) {
      // Ignore
    }
  };

  const syncOfflineCheckins = async () => {
    // Let the service worker handle this via background sync
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage('forceSyncScans');
    }
    setTimeout(checkPendingOffline, 2000);
  };

  const fetchEvent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data);
        // Cache for offline
        localStorage.setItem(`event_${eventId}`, JSON.stringify(data));
      }
    } catch (err) {
      // Try cached data
      const cached = localStorage.getItem(`event_${eventId}`);
      if (cached) {
        setEvent(JSON.parse(cached));
      }
      console.error('Failed to fetch event:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/events/${eventId}/qr/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setRecentCheckins(data.recent_checkins || []);
        // Cache stats
        localStorage.setItem(`stats_${eventId}`, JSON.stringify(data));
      }
    } catch (err) {
      // Use cached stats
      const cached = localStorage.getItem(`stats_${eventId}`);
      if (cached) {
        const data = JSON.parse(cached);
        setStats(data);
        setRecentCheckins(data.recent_checkins || []);
      }
      console.error('Failed to fetch stats:', err);
    }
  };

  const playSound = async (type) => {
    if (!soundEnabled) return;
    try {
      if (type === 'success' && successSound.current) {
        await successSound.current();
      } else if (type === 'error' && errorSound.current) {
        await errorSound.current();
      }
    } catch (e) {
      console.log('Sound play failed:', e);
    }
  };

  const startScanner = async () => {
    try {
      setScanning(true);
      setResult(null);
      setError(null);

      html5QrCodeRef.current = new Html5Qrcode("qr-reader-fullscreen");
      
      const config = {
        fps: 15,
        qrbox: { width: 280, height: 280 },
        aspectRatio: 1.0,
        disableFlip: false
      };

      // Use selected camera or back camera
      const cameraConfig = selectedCamera 
        ? { deviceId: { exact: selectedCamera } }
        : { facingMode: "environment" };

      await html5QrCodeRef.current.start(
        cameraConfig,
        config,
        onScanSuccess,
        onScanFailure
      );
    } catch (err) {
      console.error('Camera error:', err);
      setError('Failed to start camera. Please allow camera access and try again.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (e) {
        console.log('Scanner stop error:', e);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const switchCamera = async () => {
    if (cameraList.length < 2) return;
    
    const currentIndex = cameraList.findIndex(c => c.id === selectedCamera);
    const nextIndex = (currentIndex + 1) % cameraList.length;
    const nextCamera = cameraList[nextIndex];
    
    setSelectedCamera(nextCamera.id);
    
    if (scanning) {
      await stopScanner();
      setTimeout(() => startScanner(), 100);
    }
  };

  const onScanSuccess = useCallback(async (decodedText) => {
    // Pause scanner to prevent multiple scans
    if (html5QrCodeRef.current) {
      await html5QrCodeRef.current.pause();
    }

    try {
      const token = getToken();
      
      if (!isOnline) {
        // Queue for offline sync
        queueOfflineAction(
          `${API_URL}/api/events/${eventId}/qr/scan`,
          'POST',
          { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          JSON.stringify({ qr_data: decodedText })
        );
        
        playSound('success');
        setResult({
          success: true,
          offline: true,
          message: 'Saved offline - will sync when online',
          delegate: { name: 'Offline Check-in' }
        });
        
        setPendingOffline(prev => prev + 1);
      } else {
        // First do the check-in
        const response = await fetch(`${API_URL}/api/events/${eventId}/qr/scan`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ qr_data: decodedText })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          playSound('success');
          
          // Now fetch full delegate details using the lookup endpoint
          try {
            const lookupResponse = await fetch(
              `${API_URL}/api/events/${eventId}/qr/lookup/${encodeURIComponent(decodedText)}`,
              {
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
            
            if (lookupResponse.ok) {
              const lookupData = await lookupResponse.json();
              setResult({
                success: true,
                delegate: lookupData.delegate,
                message: data.message
              });
            } else {
              // Fallback to basic data from check-in response
              setResult({
                success: true,
                delegate: data.delegate,
                message: data.message
              });
            }
          } catch (lookupErr) {
            // Fallback to basic data
            setResult({
              success: true,
              delegate: data.delegate,
              message: data.message
            });
          }
          
          fetchStats();
        } else {
          playSound('error');
          setResult({
            success: false,
            error: data.error || data.detail,
            message: data.message || data.detail || 'Check-in failed',
            delegate: data.delegate
          });
        }
      }
    } catch (err) {
      console.error('Scan error:', err);
      // Network error - queue offline
      const token = getToken();
      queueOfflineAction(
        `${API_URL}/api/events/${eventId}/qr/scan`,
        'POST',
        { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        JSON.stringify({ qr_data: decodedText })
      );
      
      playSound('success');
      setResult({
        success: true,
        offline: true,
        message: 'Saved offline - will sync when online',
        delegate: { name: 'Offline Check-in' }
      });
      setPendingOffline(prev => prev + 1);
    }

    // Auto-resume after 5 seconds (increased to allow viewing details)
    setTimeout(() => {
      setResult(null);
      if (html5QrCodeRef.current && scanning) {
        html5QrCodeRef.current.resume();
      }
    }, 5000);
  }, [eventId, scanning, isOnline]);

  const onScanFailure = () => {
    // Ignore - just means no QR found in frame
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.log);
    } else {
      document.exitFullscreen().catch(console.log);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="checkin-station">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">{event?.name || t('checkIn.title')}</h1>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <span>{t('checkIn.title')}</span>
              {!isOnline && (
                <Badge variant="outline" className="text-amber-400 border-amber-400 text-xs">
                  <WifiOff className="h-3 w-3 mr-1" />
                  {t('offline.youAreOffline')}
                </Badge>
              )}
              {pendingOffline > 0 && (
                <Badge variant="outline" className="text-blue-400 border-blue-400 text-xs">
                  <CloudOff className="h-3 w-3 mr-1" />
                  {pendingOffline} {t('checkIn.pendingSync')}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <LanguageSwitcher variant="ghost" showLabel={false} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="text-white border-gray-600 hover:bg-gray-700"
            title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            className="text-white border-gray-600 hover:bg-gray-700 hidden md:flex"
            title="Fullscreen"
          >
            <Maximize className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white border-gray-600 hover:bg-gray-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-80px)]">
        {/* Scanner Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          {!scanning ? (
            <div className="text-center">
              <div className="relative inline-block mb-8">
                <div className="w-32 h-32 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Camera className="h-16 w-16 text-white" />
                </div>
                <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-2">
                  <ScanLine className="h-6 w-6 text-white" />
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-4">{t('checkIn.readyToScan')}</h2>
              <p className="text-gray-400 mb-8 max-w-sm">
                {t('checkIn.scanInstructions')}
              </p>
              <Button 
                onClick={startScanner} 
                size="lg"
                className="text-xl px-8 py-6 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                data-testid="start-scanner-btn"
              >
                <Camera className="h-6 w-6 mr-2" />
                {t('checkIn.startScanner')}
              </Button>
              
              {cameraList.length > 1 && (
                <p className="text-gray-500 text-sm mt-4">
                  {cameraList.length} cameras available
                </p>
              )}
            </div>
          ) : (
            <div className="relative w-full max-w-lg">
              {/* Scanner viewport */}
              <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl">
                <div 
                  id="qr-reader-fullscreen" 
                  ref={scannerContainerRef}
                  className="w-full aspect-square"
                />
                
                {/* Scan overlay animation */}
                {!result && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 border-4 border-white/20 m-8 rounded-lg" />
                    <div className="absolute left-8 right-8 top-1/3 h-0.5 bg-gradient-to-r from-transparent via-indigo-400 to-transparent animate-pulse" />
                  </div>
                )}
                
                {/* Result Overlay */}
                {result && (
                  <div 
                    className={`absolute inset-0 flex items-center justify-center ${
                      result.success 
                        ? result.offline 
                          ? 'bg-amber-500/95' 
                          : 'bg-green-500/95' 
                        : 'bg-red-500/95'
                    }`}
                    data-testid="scan-result"
                  >
                    <div className="text-center p-6 max-w-md w-full">
                      <div className="mb-3">
                        {result.success ? (
                          result.offline ? (
                            <CloudOff className="h-16 w-16 mx-auto text-white" />
                          ) : (
                            <CheckCircle className="h-16 w-16 mx-auto text-white" />
                          )
                        ) : (
                          <XCircle className="h-16 w-16 mx-auto text-white" />
                        )}
                      </div>
                      <div className="text-2xl font-bold mb-2 text-white">
                        {result.success 
                          ? result.offline 
                            ? 'Queued Offline' 
                            : 'Welcome!' 
                          : result.error === 'already_checked_in' 
                            ? 'Already Checked In' 
                            : 'Check-in Failed'
                        }
                      </div>
                      {result.delegate && (
                        <div className="text-white/90 space-y-2">
                          {/* Name */}
                          <div className="flex items-center justify-center gap-2 text-xl font-semibold">
                            <User className="h-5 w-5" />
                            {result.delegate.name}
                          </div>
                          
                          {/* Company & Title */}
                          {result.delegate.company && (
                            <div className="flex items-center justify-center gap-2 text-base text-white/80">
                              <Building className="h-4 w-4" />
                              {result.delegate.company}
                              {result.delegate.title && <span>• {result.delegate.title}</span>}
                            </div>
                          )}
                          
                          {/* Registration & Badge Info */}
                          {(result.delegate.registration_number || result.delegate.badge_code) && (
                            <div className="text-sm text-white/70">
                              Reg#: {result.delegate.registration_number || result.delegate.badge_code || result.delegate.id?.substring(0,8).toUpperCase()}
                            </div>
                          )}
                          
                          {/* Tier */}
                          {result.delegate.tier && (
                            <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                              {result.delegate.tier?.name || result.delegate.tier}
                            </div>
                          )}
                          
                          {/* Province/District */}
                          {(result.delegate.province || result.delegate.district) && (
                            <div className="text-sm text-white/70">
                              📍 {[result.delegate.province, result.delegate.district].filter(Boolean).join(', ')}
                            </div>
                          )}
                          
                          {/* Meals Entitlement */}
                          {result.delegate.meals && typeof result.delegate.meals === 'object' && (
                            <div className="mt-3 p-3 bg-white/10 rounded-lg">
                              <div className="text-sm font-medium mb-2">Meals Entitlement</div>
                              <div className="flex justify-center gap-4 text-sm">
                                <span className={result.delegate.meals.breakfast !== 'No' ? 'text-white' : 'text-white/40'}>
                                  🍳 {result.delegate.meals.breakfast}
                                </span>
                                <span className={result.delegate.meals.lunch !== 'No' ? 'text-white' : 'text-white/40'}>
                                  🥗 {result.delegate.meals.lunch}
                                </span>
                                <span className={result.delegate.meals.dinner !== 'No' ? 'text-white' : 'text-white/40'}>
                                  🍽️ {result.delegate.meals.dinner}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          {/* Accommodation */}
                          {result.delegate.accommodation && (
                            <div className="text-sm text-white/80 flex items-center justify-center gap-1">
                              🏨 {result.delegate.accommodation.room_number 
                                ? `Room ${result.delegate.accommodation.room_number}` 
                                : result.delegate.accommodation.details || 'Accommodation Included'}
                            </div>
                          )}
                          
                          {/* Payment Status */}
                          {result.delegate.payment_status && (
                            <div className={`text-sm ${
                              result.delegate.payment_status.toLowerCase().includes('paid') 
                                ? 'text-white' 
                                : 'text-amber-200'
                            }`}>
                              💳 Payment: {result.delegate.payment_status}
                            </div>
                          )}
                        </div>
                      )}
                      {result.offline && (
                        <p className="text-white/80 text-sm mt-2">{t('checkIn.willSyncWhenOnline')}</p>
                      )}
                      {!result.success && !result.delegate && (
                        <p className="text-white/80 text-sm mt-2">
                          {result.message || 'No registration found for this QR code'}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Scanner controls */}
              <div className="mt-4 flex justify-center gap-3">
                {cameraList.length > 1 && (
                  <Button 
                    variant="outline" 
                    onClick={switchCamera} 
                    className="text-white border-gray-600 hover:bg-gray-700"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('checkIn.switchCamera')}
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={stopScanner} 
                  className="text-white border-gray-600 hover:bg-gray-700"
                  data-testid="stop-scanner-btn"
                >
                  <CameraOff className="h-4 w-4 mr-2" />
                  {t('checkIn.stopScanner')}
                </Button>
              </div>
            </div>
          )}

          {error && (
            <Alert className="mt-4 bg-red-900/50 border-red-700 max-w-lg">
              <XCircle className="h-4 w-4 text-red-400" />
              <AlertDescription className="text-red-200">
                {error}
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Stats Sidebar */}
        <div className="w-full lg:w-80 bg-gray-800 p-4 overflow-y-auto">
          {/* Connection Status */}
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
            isOnline ? 'bg-green-900/30 text-green-400' : 'bg-amber-900/30 text-amber-400'
          }`}>
            {isOnline ? (
              <>
                <Wifi className="h-4 w-4" />
                <span className="text-sm">{t('checkIn.onlineStatus')}</span>
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4" />
                <span className="text-sm">{t('checkIn.offlineStatus')}</span>
              </>
            )}
          </div>

          {/* Stats */}
          {stats && (
            <Card className="bg-gray-700 border-gray-600 mb-4">
              <CardContent className="p-4">
                <h3 className="text-lg font-bold mb-4 text-white">{t('checkIn.checkInProgress')}</h3>
                
                {/* Progress Circle */}
                <div className="flex justify-center mb-4">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="#374151"
                        strokeWidth="12"
                        fill="none"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="#10B981"
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray={`${(stats.checkin_rate / 100) * 352} 352`}
                        strokeLinecap="round"
                        className="transition-all duration-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold text-white">{stats.checkin_rate}%</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-green-400">{stats.checked_in}</div>
                    <div className="text-sm text-gray-400">Checked In</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-blue-400">{stats.total_confirmed}</div>
                    <div className="text-sm text-gray-400">Total</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending Offline */}
          {pendingOffline > 0 && (
            <Card className="bg-amber-900/30 border-amber-700 mb-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-amber-400">
                    <CloudOff className="h-5 w-5" />
                    <span className="font-medium">{pendingOffline} {t('checkIn.pendingSync')}</span>
                  </div>
                  {isOnline && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={syncOfflineCheckins}
                      className="text-amber-400 border-amber-600 hover:bg-amber-900/50"
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      {t('offline.syncing').replace('...', '')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Check-ins */}
          <Card className="bg-gray-700 border-gray-600">
            <CardContent className="p-4">
              <h3 className="text-lg font-bold mb-4 text-white">{t('checkIn.recentCheckins')}</h3>
              {recentCheckins.length === 0 ? (
                <p className="text-gray-400 text-center py-4">{t('checkIn.notFound')}</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {recentCheckins.slice(0, 10).map((checkin, index) => (
                    <div 
                      key={index} 
                      className="flex justify-between items-center py-2 border-b border-gray-600 last:border-0"
                    >
                      <div>
                        <span className="font-medium text-white block">{checkin.name}</span>
                        {checkin.company && (
                          <span className="text-xs text-gray-400">{checkin.company}</span>
                        )}
                      </div>
                      <span className="text-sm text-gray-400">
                        {new Date(checkin.checked_in_at).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CheckinStation;
